import { useCallback, useEffect, useState } from 'react';
import { Bell, BellOff, X } from '@/components/icons';
import {
  fireNotification,
  getNotificationPermission,
  requestNotificationPermission,
  type NotifyPermission,
} from '@/lib/notify';

/**
 * Topbar chip that surfaces (and requests) permission for desktop
 * notifications. Hidden when the API isn't supported. Once granted it
 * collapses to a small "on" indicator plus a test button; when denied,
 * clicking it opens an in-app modal with browser-specific unblock steps.
 */
export function NotificationPermissionChip() {
  const [perm, setPerm] = useState<NotifyPermission>(() => getNotificationPermission());
  const [showBlockedHelp, setShowBlockedHelp] = useState(false);

  // Re-read on focus in case the user changed the browser setting in a
  // different tab / permission UI. Permissions API `onchange` isn't
  // universal, so this is the pragmatic fallback.
  useEffect(() => {
    const onFocus = () => setPerm(getNotificationPermission());
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const onEnableClick = useCallback(async () => {
    if (perm !== 'default') return;
    const result = await requestNotificationPermission();
    setPerm(result);
    if (result === 'granted') {
      // Confirm the pipeline the moment permission is granted so the user
      // gets immediate feedback that it works.
      fireNotification('Notifications enabled', {
        body: 'You’ll get a desktop popup when a workflow step completes.',
        tag: 'welcome',
      });
    }
  }, [perm]);

  if (perm === 'unsupported') return null;

  // When granted, we don't render a standalone chip — the Test button
  // lives inside the NotificationBell dropdown header instead.
  if (perm === 'granted') return null;

  const common =
    'inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-bold transition md:px-3.5';

  if (perm === 'denied') {
    return (
      <>
        <button
          type="button"
          onClick={() => setShowBlockedHelp(true)}
          className={common + ' border-danger/30 bg-danger/10 text-danger hover:bg-danger/20'}
          title="Notifications are blocked — click for unblock steps"
        >
          <BellOff size={14} strokeWidth={2.25} />
          <span className="hidden md:inline">Blocked</span>
        </button>
        {showBlockedHelp && <BlockedHelpModal onClose={() => setShowBlockedHelp(false)} />}
      </>
    );
  }

  // 'default' — actionable request
  return (
    <button
      type="button"
      onClick={onEnableClick}
      className={
        common +
        ' border-[var(--border-color)] bg-white/5 text-[var(--text-secondary)] hover:bg-accent/10 hover:text-accent'
      }
      title="Enable desktop notifications for step-complete alerts"
    >
      <Bell size={14} strokeWidth={2.25} />
      <span className="hidden md:inline">Enable notifications</span>
    </button>
  );
}

export function BlockedHelpModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-extrabold text-[var(--text-primary)]">
            <BellOff size={16} className="text-danger" /> Notifications are blocked
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
          >
            <X size={16} />
          </button>
        </div>
        <p className="mb-3 text-xs text-[var(--text-secondary)]">
          This site was set to block notifications. Reset it in Chrome and reload:
        </p>
        <ol className="mb-3 list-decimal space-y-2 pl-5 text-xs text-[var(--text-primary)]">
          <li>
            Click the <strong>padlock icon</strong> on the left of the address bar
            (next to <code className="rounded bg-white/5 px-1">localhost:5173</code>).
          </li>
          <li>
            Click <strong>Site settings</strong>.
          </li>
          <li>
            Under <strong>Notifications</strong>, change from <em>Block</em> to
            <strong> Allow</strong>.
          </li>
          <li>
            Come back to this tab and <strong>reload</strong> — this chip will turn green.
          </li>
          <li>
            Then click the green chip to send a test to your Windows notification
            center.
          </li>
        </ol>
        <p className="mb-1 text-[11px] font-bold text-[var(--text-secondary)]">
          Also confirm on Windows:
        </p>
        <ul className="list-disc space-y-1 pl-5 text-[11px] text-[var(--text-muted)]">
          <li>Settings → System → Notifications is <strong>On</strong>.</li>
          <li>Under &ldquo;Notifications from apps&rdquo;, Google Chrome is <strong>On</strong>.</li>
          <li>Focus assist / Do Not Disturb is <strong>Off</strong>.</li>
        </ul>
      </div>
    </div>
  );
}
